<script setup lang="ts">
import { useAuth } from '@clerk/vue'
import { ref, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Sidebar from '@/components/Sidebar.vue'
import TrialBanner from '@/components/TrialBanner.vue'
import { useImpersonation } from '@/composables/useImpersonation'

const router = useRouter()
const route = useRoute()
const { isLoaded, isSignedIn } = useAuth()
const { impersonating, impersonatedOrgName, exitImpersonation, checkStatus } = useImpersonation()

const sidebarOpen = ref(false)

// Close sidebar on route change (mobile)
watch(() => route.path, () => {
  sidebarOpen.value = false
})

// Redirect to sign-in when not authenticated
watch([isLoaded, isSignedIn], ([loaded, signedIn]) => {
  if (loaded && !signedIn) {
    router.replace('/sign-in')
  }
}, { immediate: true })

// Check impersonation status on mount
onMounted(() => {
  checkStatus()
})
</script>

<template>
  <!-- Loading state while Clerk initializes -->
  <div v-if="!isLoaded" class="min-h-screen bg-surface flex items-center justify-center">
    <div class="animate-sync-pulse text-text-muted font-label text-label-md uppercase tracking-[0.05em]">
      Loading...
    </div>
  </div>

  <!-- Not signed in: show nothing, Clerk handles redirect -->
  <div v-else-if="!isSignedIn" class="min-h-screen bg-surface flex items-center justify-center">
    <div class="text-text-muted font-label text-label-md uppercase tracking-[0.05em]">
      Redirecting to sign in...
    </div>
  </div>

  <!-- Signed in: show dashboard layout -->
  <div v-else class="min-h-screen bg-surface">
    <!-- Mobile header bar -->
    <div class="lg:hidden fixed top-0 left-0 right-0 z-40 bg-surface-lowest px-md py-sm flex items-center justify-between">
      <div class="flex items-center gap-sm">
        <img src="/favicon.svg" alt="FlagDrop" class="w-6 h-6" />
        <span class="font-display text-body-md font-bold text-text-primary">FlagDrop</span>
      </div>
      <button
        class="btn-ghost !p-xs"
        @click="sidebarOpen = !sidebarOpen"
        aria-label="Toggle menu"
      >
        <svg v-if="!sidebarOpen" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        <svg v-else class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Backdrop overlay (mobile) -->
    <Transition
      enter-active-class="transition-opacity duration-short ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-short ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="sidebarOpen"
        class="lg:hidden fixed inset-0 z-30 bg-black/50"
        @click="sidebarOpen = false"
      />
    </Transition>

    <!-- Sidebar -->
    <div
      :class="[
        'fixed left-0 top-0 bottom-0 w-[220px] z-40 transition-transform duration-medium ease-in-out',
        'lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <Sidebar />
    </div>

    <!-- Impersonation banner -->
    <div
      v-if="impersonating"
      class="fixed top-0 left-0 right-0 z-50 bg-warning/90 text-surface-lowest px-md py-sm flex items-center justify-center gap-md text-body-sm font-label"
    >
      <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
      </svg>
      <span>
        Viewing as <strong>{{ impersonatedOrgName }}</strong>
        <span class="hidden sm:inline"> &mdash; Read-only mode</span>
      </span>
      <button
        class="ml-sm px-sm py-[2px] rounded-md bg-surface-lowest/20 hover:bg-surface-lowest/40 text-surface-lowest font-label text-label-sm uppercase tracking-[0.05em] transition-colors duration-short"
        @click="exitImpersonation"
      >
        Exit Impersonation
      </button>
    </div>

    <!-- Main content -->
    <main
      class="min-h-screen lg:ml-[220px] pt-[52px] lg:pt-0"
      :class="{ 'pt-[88px] lg:pt-[36px]': impersonating }"
    >
      <div class="p-md sm:p-lg lg:p-xl">
        <TrialBanner />
        <slot />
      </div>
    </main>
  </div>
</template>
